import { useEffect, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { loadSession } from "../auth";
import { listMyAssignments, getProgress } from "../services/api";
import { shouldBlockFeatureLoads } from "../utils/onboardingGate";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Trainings() {
  const { t } = useTranslation();
  const user = loadSession()?.user;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // progressById: { [trainingId]: lastStepIndex }
  const [progressById, setProgressById] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);

    // If onboarding/tenant gate is active, avoid calling assignments/progress
    if (shouldBlockFeatureLoads()) {
      setItems([]);
      setProgressById({});
      setLoading(false);
      return () => { alive = false; };
    }

    (async () => {
      try {
        const res = await listMyAssignments(user.id);
        if (!alive) return;
        setItems(Array.isArray(res) ? res : []);
        // fetch per-training progress
        const ids = (Array.isArray(res) ? res : []).map((it) => it.trainingId);
        const results = await Promise.allSettled(ids.map((id) => getProgress(id)));
        if (!alive) return;
        const map = {};
        results.forEach((r, idx) => {
          const id = ids[idx];
          if (r.status === "fulfilled" && r.value && typeof r.value.lastStepIndex === "number") {
            map[id] = r.value.lastStepIndex;
          } else {
            map[id] = 0; // default: not started
          }
        });
        setProgressById(map);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user.id]);

  const renderProgress = (it) => {
    const stepsCount =
      it.stepsCount || (Array.isArray(it.steps) ? it.steps.length : 0) || 0;
    const lastStepIndex = progressById[it.trainingId] ?? 0;
    // completed steps = current index (clamped)
    const completed = Math.max(0, Math.min(lastStepIndex, Math.max(0, stepsCount - 1)));
    const pct = stepsCount > 0 ? (completed / stepsCount) * 100 : 0;

    return (
      <div className="mt-2">
        <div className="w-40 h-1.5 bg-slate-200 rounded-full">
          <div
            className="h-1.5 bg-blue-600 rounded-full transition-all"
            style={{ width: `${pct}%` }}
            aria-label={t("trainings.progress_label", { pct: Math.round(pct) })}
          />
        </div>
        <div className="text-xs text-slate-400 mt-1">
          {t("trainings.completed_of_total", { completed, total: stepsCount })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-slate-900 mb-5">{t("trainings.title")}</h1>
      <Card>
        {loading ? (
          <div className="text-sm text-slate-500">{t("trainings.loading")}</div>
        ) : items.length ? (
          <ul className="divide-y divide-slate-100">
            {items.map((it) => {
              const stepsCount =
                it.stepsCount || (Array.isArray(it.steps) ? it.steps.length : 0) || 0;
              const hasProgress = (progressById[it.trainingId] ?? 0) > 0;
              return (
                <li key={it.trainingId} className="py-4 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{it.title}</div>
                    <div className="text-sm text-slate-500">{it.description}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {t("trainings.steps_count", { count: stepsCount })}
                    </div>
                    {renderProgress(it)}
                  </div>
                  <Link to={`/training/${it.trainingId}`}>
                    <Button variant={hasProgress ? "secondary" : "primary"}>
                      {hasProgress ? t("trainings.btn_continue") : t("trainings.btn_start")}
                    </Button>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-sm text-slate-500 py-4 text-center">{t("trainings.none")}</div>
        )}
      </Card>
    </div>
  );
}
